import * as Sentry from "@sentry/react";
import BN from "bn.js";
import { noop } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { combineLatest, EMPTY, merge, Observable, of, Subject } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  skip,
  startWith,
  switchMap,
  switchMapTo,
  take,
  withLatestFrom,
} from "rxjs/operators";
import { None, Option, Some } from "space-monad";
import Web3 from "web3";
import { invert } from "../../../utils/utils";
import { Billing } from "../../billing/reducer";
import { AllowedBlockchains } from "../../chain/models/internal";
import { ensureResource } from "../../common/fetchable";
import { NotificationActions } from "../../notifications/actions";
import { store$ } from "../../utils";
import { WalletsActions } from "../actions";
import { WalletsService } from "../service";
import { bscNetworkSetup } from "./binance";
import { chainsMap, chainSpecs } from "./chain-config";
import { ethNetworkSetup } from "./eth";
import { mumbaiNetworkSetup } from "./mumbai";
import { NetworkSetup } from "./network-setup";
import { polygonNetworkSetup } from "./polygon";
import { ropstenNetworkSetup } from "./ropsten";

// An observable for the ethereum window object
export const ethereum$ = of(1).pipe(
  mapTo(window),
  map((w) => {
    return Option((w as any).ethereum);
  }),
  shareReplay(1)
);

export const enableWeb3 = new Subject<void>();

// An observable for the web3 class
export const web3$: Observable<Option<any>> = merge(
  ethereum$, // all instances of ethereum$
  enableWeb3.pipe(
    // all instances of ethereum$ after we fire the enable subject
    switchMapTo(ethereum$),
    switchMap((eth) =>
      eth.fold(
        () => of(None),
        async (someEth) => {
          await someEth.enable();
          return Some(someEth);
        }
      )
    )
  )
).pipe(
  map((eth) =>
    eth.fold(
      () => None,
      (someEth) => Some(new Web3(someEth))
    )
  ),
  shareReplay(1)
);

const verified = new Subject<void>();
// Listening to changes of account/network too
const account$ = merge(
  of(1),
  ethereum$.pipe(
    switchMap((ethOpt) =>
      ethOpt.fold(
        () => EMPTY,
        (eth) =>
          new Observable((sub) => eth.on("chainChanged", () => sub.next()))
      )
    )
  ),
  ethereum$.pipe(
    switchMap((ethOpt) =>
      ethOpt.fold(
        () => EMPTY,
        (eth) =>
          new Observable((sub) => eth.on("accountsChanged", () => sub.next()))
      )
    )
  ),
  verified
).pipe(
  switchMapTo(web3$),
  switchMap((web3Some) =>
    web3Some.fold(
      () => of(None),
      async (web3) => {
        const accounts = await web3.eth.getAccounts();
        const coinbase = await web3.eth.getCoinbase();
        const networkId = await web3.eth.net.getId();

        if (coinbase === null || coinbase === undefined) return None;
        return Some({ accounts, coinbase, networkId });
      }
    )
  ),
  shareReplay(1)
);

const verify = new Subject<void>();

// This stream will only return Errors if present
const verify$ = verify.pipe(
  withLatestFrom(account$),
  switchMap(([_, accountOpt]) =>
    accountOpt.fold(
      () => of(None),
      async ({ coinbase, networkId }) => {
        try {
          const nonce = await WalletsService.getWalletNonce(coinbase);
          const signature = await handleSignMessage(coinbase, nonce);
          await WalletsService.addWallet({
            address: coinbase,
            signature,
            chain: chainsMap[networkId],
          });
          return None;
        } catch (e: any) {
          return Some(e);
        }
      }
    )
  ),
  shareReplay(1)
);

// Trigger refetch upon verification
verify$.pipe(filter((x) => x.isDefined())).subscribe(() => verified.next());

const walletVerified$ = combineLatest([
  merge(of(1), verify$).pipe(switchMapTo(account$)),
  store$.pipe(
    map((state) => state.auth.login),
    distinctUntilChanged(
      (a, b) =>
        a.status === b.status &&
        (a.status === "ready" && b.status === "ready"
          ? a.data.accessToken.authenticated ===
            b.data.accessToken.authenticated
          : true)
    )
  ),
]).pipe(
  switchMap(([accountOpt, login]) =>
    accountOpt.fold(
      () => of(None),
      async (account) => {
        if (login.status !== "ready" || !login.data.accessToken.authenticated)
          return None;
        try {
          return Some(
            await WalletsService.queryWallet({
              address: account.coinbase,
              chain: chainsMap[account.networkId],
            })
          );
        } catch (err: any) {
          return None;
        }
      }
    )
  ),
  shareReplay(1)
);

export const refreshWalletBalance = new Subject<void>();

const walletBalance$ = combineLatest([
  merge(refreshWalletBalance, of(1)),
  account$,
]).pipe(
  map(([, accountOpt]) => accountOpt),
  withLatestFrom(web3$),
  switchMap(([accountOpt, web3Opt]) =>
    Option.all([accountOpt, web3Opt]).fold(
      () => of(None),
      async ([{ coinbase, networkId }, web3]) => {
        const networkConfiguration = configurations[chainsMap[networkId]];
        if (!(networkConfiguration && networkConfiguration.contracts.skrt))
          return None;
        // TODO: throw error

        const { address, abi } = networkConfiguration.contracts.skrt;

        const skrtContract = new web3.eth.Contract(abi, address);
        const symbol = await skrtContract.methods.symbol().call();
        const amount = web3.utils.toBN(
          await skrtContract.methods.balanceOf(coinbase).call()
        );
        return Some({
          symbol,
          amount,
        });
      }
    )
  ),
  shareReplay(1)
);

const purchase$: Observable<
  Option<(amount: BN) => Promise<any>>
> = combineLatest([web3$, account$]).pipe(
  map(([web3Opt, accountOpt]) =>
    Option.all([web3Opt, accountOpt]).map(
      ([web3, account]) => async (amount: BN) => {
        const skrtAddresses = await ensureResource(Billing.skrtAddresses);

        const to = skrtAddresses.find(
          (x) => x.chain === chainsMap[account.networkId]
        );

        const chainConfig = configurations[chainsMap[account.networkId]];

        if (chainConfig === undefined)
          throw new Error("Chain Configuration not Found");
        if (to === undefined) throw new Error("Destination Address not Found");

        const contract = new web3.eth.Contract(
          chainConfig.contracts.usdt.abi,
          chainConfig.contracts.usdt.address
        );

        const multiplier = new BN(
          `1${Array(chainConfig.contracts.usdt.decimals - 2)
            .fill(0)
            .join("")}`
        );

        const gasPriceInWei = await chainSpecs[account.networkId].gasPrice();

        return contract.methods
          .transfer(to.address, amount.mul(multiplier))
          .send({
            from: account.coinbase,
            gasPrice: gasPriceInWei.get(),
          });
      }
    )
  )
);

const isLoading$ = merge(
  enableWeb3.pipe(mapTo(true)),
  verify.pipe(mapTo(true)),
  enableWeb3.pipe(switchMapTo(web3$), skip(1), mapTo(false)),
  verify.pipe(switchMapTo(verify$), mapTo(false))
).pipe(startWith(false), shareReplay(1));

const actualState$ = combineLatest([
  ethereum$,
  account$,
  walletVerified$,
  walletBalance$,
  isLoading$,
  purchase$,
]).pipe(shareReplay(1));

type Bar = "UNAVAILABLE" | "DISCONNECTED" | "CONNECTED" | "LOADING";

export type BrowserWalletState = {
  isEthAvailable: boolean;
  accounts: Option<{
    accounts: any;
    coinbase?: any;
    networkId: any;
  }>;
  isVerified: boolean;
  balance: Option<{ symbol: string; amount: BN }>;
  connect: () => void;
  verify: () => void;
  purchase: Option<(amount: BN) => Promise<any>>;
  isLoading: boolean;
  state: Bar;
};

const defaultBrowserWalletState: BrowserWalletState = {
  isEthAvailable: false,
  accounts: None,
  isVerified: false,
  balance: None,
  connect: noop,
  verify: noop,
  purchase: None,
  isLoading: false,
  state: "UNAVAILABLE",
};

export const useBrowserWallet = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<BrowserWalletState>(
    defaultBrowserWalletState
  );
  useEffect(() => {
    const sub2 = verify$.pipe().subscribe((errOpt: Option<any>) => {
      errOpt.fold(
        () => {
          dispatch(WalletsActions.resetWallets);
          dispatch(WalletsActions.resetTotalBalance);
          dispatch(WalletsActions.resetWalletsSummary);
        },
        (err) => {
          dispatch(
            err.code === 4001
              ? NotificationActions.setNotification({
                  title: "permission-denied-from-metamask",
                  variant: "error",
                })
              : NotificationActions.setNotification({
                  title: err.message,
                  variant: "error",
                })
          );

          Sentry.captureException(err);
        }
      );
    });

    const sub = actualState$.subscribe(
      ([
        ethOpt,
        accountOpt,
        walletVerifiedOpt,
        walletBalanceOpt,
        isLoading,
        purchaseOpt,
      ]) => {
        let bar: Bar = "UNAVAILABLE";

        if (ethOpt.isDefined()) {
          bar = "DISCONNECTED";
        }
        if (accountOpt.exists((x) => !!x.coinbase)) {
          bar = "CONNECTED";
        }
        if (isLoading) {
          bar = "LOADING";
        }
        const foo: BrowserWalletState = {
          isEthAvailable: ethOpt.isDefined(),
          accounts: accountOpt,
          isVerified: walletVerifiedOpt.exists((x) => !!x.walletFound),
          balance: walletBalanceOpt,
          connect: () => enableWeb3.next(),
          verify: () => verify.next(),
          purchase: purchaseOpt,
          isLoading,
          state: bar,
        };
        setState(foo);
      }
    );

    return () => {
      sub.unsubscribe();
      sub2.unsubscribe();
    };
  }, [dispatch]);
  return state;
};

let web3Instance: Promise<Web3>;

export type TransactionReceipt = Awaited<
  ReturnType<typeof Web3.prototype.eth.getTransactionReceipt>
>;

/**
 * This will return a singleton web3 instance.
 * @param enable should user be prompted to allow metamask
 * @returns web3 instance
 */
export const getWeb3: (enable?: boolean) => Promise<Web3> = (
  enable = false
) => {
  // If we are asking the user to enable a new instance
  if (enable) {
    web3Instance = (async () => {
      if (!(window as any).ethereum) {
        throw new Error("Ethereum doesn't exist on window");
      }
      await (window as any).ethereum.enable();
      return new Web3((window as any).ethereum);
    })();

    // Otherwise check if the web3 instance is already populated (we should avoid asking the user to enable it unless required)
  } else if (web3Instance === undefined) {
    web3Instance = (async () => {
      if (!(window as any).ethereum) {
        throw new Error("Ethereum doesn't exist on window");
      }
      return new Web3((window as any).ethereum);
    })();
  }

  return web3Instance;
};

export const handleSignMessage = async (address: string, nonce: string) => {
  const web3 = await getWeb3();
  const signature = await web3.eth.personal.sign(
    nonce,
    address,
    "" // MetaMask will ignore the password argument here
  );
  return signature;
};

export const initialiseWallet: () => Promise<string> = async () => {
  const web3 = await getWeb3();
  const coinbase = await web3.eth.getCoinbase();
  if (coinbase === null) throw new Error("No default account found");
  return coinbase.toLocaleLowerCase();
};

const invertedChainMap = invert(chainsMap);

export const requestChainChange = async (chain: AllowedBlockchains) => {
  const chainId = invertedChainMap[chain];
  if (chainId === undefined) throw new Error("Chain Not Found");

  const web3Opt = await web3$.pipe(take(1)).toPromise();
  return web3Opt.fold(
    () => {
      return;
    },
    async (web3) => {
      const switchResult = await web3.currentProvider._rpcRequest({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: chainSpecs[chainId].chainId }],
      });

      if (switchResult.error) {
        if (switchResult.error.code === -32603) {
          // Add Chain
          const addChainResult = await web3.currentProvider._rpcRequest({
            method: "wallet_addEthereumChain",
            params: [chainSpecs[chainId]],
          });

          if (addChainResult.error) throw addChainResult.error;
        } else throw switchResult.error;
      }
    }
  );
};

export const configurations: Partial<
  Record<AllowedBlockchains, NetworkSetup>
> = {
  ETH: ethNetworkSetup,
  MATIC: polygonNetworkSetup,
  ROPSTEN: ropstenNetworkSetup,
  BSC: bscNetworkSetup,
  MUMBAI: mumbaiNetworkSetup,
};

export const blockchainExplorers: Partial<
  Record<
    keyof typeof configurations,
    { tx: (id: string) => string; address: (id: string) => string }
  >
> = {
  ETH: {
    address: (id: string) => `https://etherscan.io/address/${id}`,
    tx: (id: string) => `https://etherscan.io/tx/${id}`,
  },
  ROPSTEN: {
    address: (id: string) => `https://ropsten.etherscan.io/address/${id}`,
    tx: (id: string) => `https://ropsten.etherscan.io/tx/${id}`,
  },
  MATIC: {
    address: (id: string) => `https://polygonscan.com/address/${id}`,
    tx: (id: string) => `https://polygonscan.com/tx/${id}`,
  },
  BSC: {
    address: (id: string) => `https://bscscan.com/address/${id}`,
    tx: (id: string) => `https://bscscan.com/tx/${id}`,
  },
};
